import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { settingsService, Settings } from '../services/settingsService';

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Settings) => Promise<void>;
  refreshSettings: () => Promise<void>;
  loading: boolean;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>({
    desktop_logo: "/logo-vargas-homecenter.png",
    mobile_logo: "/logocelular.png"
  });
  const [loading, setLoading] = useState(false);

  const refreshSettings = async () => {
    try {
      setLoading(true);
      const settingsData = await settingsService.getSettings();
      
      const finalSettings = {
        desktop_logo: settingsData.desktop_logo || "/logo-vargas-homecenter.png",
        mobile_logo: settingsData.mobile_logo || "/logocelular.png",
        ...settingsData
      };
      
      setSettings(finalSettings);
    } catch (error) {
      console.error("Error loading settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (newSettings: Settings) => {
    try {
      setLoading(true);
      await settingsService.updateSettings(newSettings);
      setSettings(newSettings);
    } catch (error) {
      console.error("Error updating settings:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSettings();
  }, []);

  const value = {
    settings,
    updateSettings,
    refreshSettings,
    loading
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};